import { List, Typography, ChevronRightIcon, ChevronLeftIcon, IconButton } from '@rbx/ui';
import React, { useCallback } from 'react';
import { useTranslation } from '@rbx/intl';
import throwError from '@modules/utilities/error';
import {
  TResultClickedInteraction,
  TCategoryClickedInteraction,
  TCategoryClearedInteraction,
  isRealMouseClickEvent,
} from '@modules/search/searchEvents';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import useSearchListStyles from './SearchList.styles';
import {
  placeholderEndAdornment,
  SearchListItemLink,
  SearchListItemLinkProps,
} from './SearchListItem';
import { TSearchListItem } from './types/SearchListItem';
import { SearchableContainer } from './hooks/useSearchNavigation';

export interface SearchListProps {
  showAllResultsButton?: boolean;
  isTitleClickable?: boolean;
  title?: string;
  items: TSearchListItem[];
  onClickTitle?: (interaction: TCategoryClickedInteraction) => void;
  onClearFilter?: (interaction: TCategoryClearedInteraction) => void;
  onClickItem: SearchListItemLinkProps['onClickItem'];
  onRemove?: (id: string) => void;
}

export const SearchList: React.FC<SearchListProps> = ({
  showAllResultsButton = false,
  isTitleClickable,
  title,
  items,
  onClickTitle,
  onClearFilter,
  onClickItem,
  onRemove,
}) => {
  const { classes, cx } = useSearchListStyles();
  const { translate } = useTranslation();

  const onClickLink = useCallback(
    (item: TSearchListItem, interaction: TResultClickedInteraction) => {
      onClickItem(item, interaction);
    },
    [onClickItem],
  );

  if (isTitleClickable && !onClickTitle) {
    throwError('SearchList: onClickTitle is required when isTitleClickable is true');
  }

  const onClickListTitle = (e: React.MouseEvent) => {
    if (isRealMouseClickEvent(e)) {
      onClickTitle?.(ESearchInteraction.ClickCategoryTitle);
    } else {
      onClickTitle?.(ESearchInteraction.KeyboardEnterCategoryTitle);
    }
  };

  const onClickAllResults = (e: React.MouseEvent) => {
    if (isRealMouseClickEvent(e)) {
      onClearFilter?.(ESearchInteraction.ClickCategoryTitle);
    } else {
      onClearFilter?.(ESearchInteraction.KeyboardEnterCategoryTitle);
    }
  };

  return (
    <React.Fragment>
      {!showAllResultsButton && title && (
        <div
          className={cx(classes.sectionTitleContainer, {
            [classes.sectionTitleHasButtonIcon]: isTitleClickable,
          })}>
          <Typography
            variant='tableHead'
            component='h2'
            color='primary'
            onClick={isTitleClickable ? onClickListTitle : undefined}
            className={cx(classes.sectionTitle, {
              [classes.isClickable]: isTitleClickable,
            })}>
            {title}
          </Typography>
          {isTitleClickable && (
            <IconButton
              onClick={onClickListTitle}
              aria-label={translate('Label.ClickFilter') || 'Click filter'}
              color='secondary'>
              <ChevronRightIcon />
            </IconButton>
          )}
        </div>
      )}
      {showAllResultsButton && (
        <div className={classes.sectionTitleContainerAllResults}>
          <IconButton
            onClick={onClickAllResults}
            aria-label={translate('Label.AllResults') || 'All Results'}
            color='secondary'>
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant='tableHead'
            component='h2'
            color='primary'
            className={cx(classes.sectionTitleAllResults, {
              [classes.isClickable]: isTitleClickable,
            })}
            onClick={isTitleClickable ? onClickAllResults : undefined}>
            {translate('Label.AllResults') || 'All Results'}
          </Typography>
        </div>
      )}
      <List
        className={classes.listContainer}
        data-list-container={SearchableContainer.List}
        tabIndex={-1}>
        {items.map((item) => (
          <SearchListItemLink
            key={item.id}
            item={item}
            onClickItem={onClickLink}
            onRemove={onRemove}
            className={item.className}
            ariaLabel={item.ariaLabel}
            endAdornment={placeholderEndAdornment}
          />
        ))}
      </List>
    </React.Fragment>
  );
};
